// 🌟 새로운 간단한 재정관리 시스템 타입 정의

export type Currency = 'HUF' | 'EUR' | 'USD' | 'KRW' | 'GBP' | 'CAD';

export type ExpenseCategory =
  | 'agape'        // 애찬
  | 'ministry'     // 사역지원
  | 'vehicle'      // 차량지원
  | 'utility'      // 유틸리티
  | 'department'   // 부서지원
  | 'operating'    // 경상비
  | 'relief'       // 구제비
  | 'scholarship'  // 장학금
  | 'bonus'        // 상여금
  | 'discretionary'// 판공비
  | 'event'        // 행사비
  | 'other';       // 기타

export type ExpensePaymentSource = 'church-cash' | 'personal-advance';

export type IncomeCategory =
  | 'tithe'        // 십일조
  | 'thanksgiving' // 감사헌금
  | 'special';     // 특별헌금

export type OtherIncomeCategory =
  | 'sunday'       // 주일헌금
  | 'friday'       // 금요헌금
  | 'mission'      // 선교헌금
  | 'seasonal'     // 절기헌금
  | 'building'     // 건축헌금
  | 'purpose'      // 목적헌금
  | 'other';       // 기타

export type FinanceStatus = 'pending' | 'approved' | 'reapproval' | 'superseded';

export interface FinanceEvidenceFile {
  fileName: string;
  url: string;
  path: string;
  uploadedAt: string;
  uploadedBy: string;
}

// 수입 항목 (십일조, 감사헌금)
export interface IncomeItem {
  id: string;
  name: string;           // 이름
  amount: number;         // 금액
  currency: Currency;     // 통화
  description?: string;   // 내용 (선택)
}

// 기타헌금 항목
export interface OtherIncomeItem {
  id: string;
  category: OtherIncomeCategory; // 항목
  name?: string;          // 이름 (선교헌금, 절기헌금, 목적헌금 등에서 사용)
  amount: number;         // 금액
  currency: Currency;     // 통화
  description?: string;   // 내용 (선택)
}

// 지출 항목
export interface ExpenseItem {
  id: string;
  category: ExpenseCategory;  // 항목
  amount: number;             // 금액
  currency: Currency;         // 통화
  description: string;        // 내용
  paymentSource?: ExpensePaymentSource; // 교회 현금 또는 개인 선지출
  reimbursed?: boolean;        // 개인 선지출 상환 여부
}

// 통화별 합계
export interface CurrencyTotals {
  HUF: number;
  EUR: number;
  USD: number;
  KRW: number;
  GBP: number;
  CAD: number;
}

// 메인 재정 데이터
export interface FinanceRecord {
  id: string;
  date: string;                    // YYYY-MM-DD

  // 수입 항목들
  titheItems: IncomeItem[];        // 십일조
  thanksgivingItems: IncomeItem[]; // 감사헌금
  otherIncomeItems: OtherIncomeItem[]; // 기타헌금

  // 지출 항목들
  expenseItems: ExpenseItem[];     // 지출

  // 합계들
  incomeTotals: CurrencyTotals;    // 수입합계
  expenseTotals: CurrencyTotals;   // 지출합계
  netTotals: CurrencyTotals;       // 순손익

  // 검색 필드들 (선택적 - 기존 데이터 호환성)
  participantNames?: string[];     // 참여자 이름들 ["김철수", "이영희"]
  offeringTypes?: string[];        // 헌금 종류들 ["tithe", "mission", "building"]

  // 메타 정보
  status: FinanceStatus;           // 상태
  createdBy: string;               // 작성자
  createdAt: Date;                 // 작성일시
  approvedBy?: string;             // 승인자
  approvedAt?: Date;               // 승인일시

  // 수정 관련
  modificationReason?: string;     // 수정 사유
  originalId?: string;             // 원본 ID (수정 시)
  modifiedAt?: Date;               // 수정일시
  modifiedBy?: string;             // 수정자
  evidenceFiles?: FinanceEvidenceFile[]; // 주간 재정 증빙 사진/파일
  deletionReason?: string;         // 삭제 대신 보존 처리한 사유
  deletedAt?: Date;
  personalAdvanceTotals?: CurrencyTotals; // 교회가 개인에게 갚아야 할 금액
}

// 폼 데이터 (입력용)
export interface FinanceFormData {
  date: string;
  titheItems: Omit<IncomeItem, 'id'>[];
  thanksgivingItems: Omit<IncomeItem, 'id'>[];
  otherIncomeItems: Omit<OtherIncomeItem, 'id'>[];
  expenseItems: Omit<ExpenseItem, 'id'>[];
}

// 카테고리 라벨 매핑
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  agape: '애찬',
  ministry: '사역지원',
  vehicle: '차량지원',
  utility: '유틸리티',
  department: '부서지원',
  operating: '경상비',
  relief: '구제비',
  scholarship: '장학금',
  bonus: '상여금',
  discretionary: '판공비',
  event: '행사비',
  other: '기타'
};

export const INCOME_CATEGORY_LABELS: Record<IncomeCategory, string> = {
  tithe: '십일조',
  thanksgiving: '감사헌금',
  special: '특별헌금'
};

export const OTHER_INCOME_CATEGORY_LABELS: Record<OtherIncomeCategory, string> = {
  sunday: '주일헌금',
  friday: '금요헌금',
  mission: '선교헌금',
  seasonal: '절기헌금',
  building: '건축헌금',
  purpose: '목적헌금',
  other: '기타'
};

// 통화 라벨 매핑
export const CURRENCY_LABELS: Record<Currency, string> = {
  HUF: 'HUF',
  EUR: 'EUR',
  USD: 'USD',
  KRW: 'KRW',
  GBP: 'GBP',
  CAD: 'CAD'
};

// 한글 통화 매핑 (자동 파싱용)
export const KOREAN_CURRENCY_MAP: Record<string, Currency> = {
  'ㄷ': 'EUR',
  'ㅕ': 'USD',
  'ㅏ': 'KRW'
};

// 검색 가능한 헌금 종류 타입
export type SearchableOfferingType =
  | 'tithe'        // 십일조
  | 'thanksgiving' // 감사헌금
  | 'sunday'       // 주일헌금
  | 'friday'       // 금요헌금
  | 'mission'      // 선교헌금
  | 'seasonal'     // 절기헌금
  | 'building'     // 건축헌금
  | 'purpose'      // 목적헌금
  | 'other';       // 기타

// 헌금 종류 한글 라벨 매핑
export const OFFERING_TYPE_LABELS: Record<SearchableOfferingType, string> = {
  tithe: '십일조',
  thanksgiving: '감사헌금',
  sunday: '주일헌금',
  friday: '금요헌금',
  mission: '선교헌금',
  seasonal: '절기헌금',
  building: '건축헌금',
  purpose: '목적헌금',
  other: '기타헌금'
};

// 헌금 종류별 검색 필터 옵션
export const OFFERING_SEARCH_OPTIONS = [
  { value: 'all', label: '전체 헌금' },
  { value: 'tithe', label: '십일조' },
  { value: 'thanksgiving', label: '감사헌금' },
  { value: 'mission', label: '선교헌금' },
  { value: 'building', label: '건축헌금' },
  { value: 'sunday', label: '주일헌금' },
  { value: 'friday', label: '금요헌금' },
  { value: 'seasonal', label: '절기헌금' },
  { value: 'purpose', label: '목적헌금' },
  { value: 'other', label: '기타헌금' }
];
